/**
 * CourseSwitcherButton
 *
 * Icon button that opens a dropdown to switch between enrolled courses.
 * Shows a language-swap icon; dropdown has radio-style course options.
 * Hidden when the user has only one course.
 *
 * Used in ConversationHubScreen header.
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
} from 'react-native';
import Ionicons from './ui/Ionicons';
import { useTranslation } from 'react-i18next';

import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import {
  selectCoursePreferences,
  selectActiveCourseId,
  selectSwitchActiveCourse,
} from '../store/userPreferencesSelectors';
import { getLanguageDisplayName } from '../i18n';
import { getBaseLanguage } from '../types/locale';
import { FS_SM, FS_XS } from '../theme';

interface CourseSwitcherButtonProps {
  size: number;
  color: string;
}

export const CourseSwitcherButton = React.memo(function CourseSwitcherButton({
  size,
  color,
}: CourseSwitcherButtonProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { i18n, t } = useTranslation();

  const coursePreferences = useUserPreferencesStore(selectCoursePreferences);
  const activeCourseId = useUserPreferencesStore(selectActiveCourseId);
  const switchActiveCourse = useUserPreferencesStore(selectSwitchActiveCourse);

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const buttonRef = useRef<View>(null);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const hasMultipleCourses = coursePreferences.length > 1;

  const handleOpen = useCallback(() => {
    if (!hasMultipleCourses) return;
    buttonRef.current?.measureInWindow((x, y, width, height) => {
      setButtonLayout({ x, y, width, height });
      setDropdownVisible(true);
    });
  }, [hasMultipleCourses]);

  const handleSelect = useCallback((courseId: string) => {
    setDropdownVisible(false);
    switchActiveCourse(courseId);
  }, [switchActiveCourse]);

  const handleClose = useCallback(() => {
    setDropdownVisible(false);
  }, []);

  if (!hasMultipleCourses) return null;

  const uiLocale = i18n.language;

  return (
    <>
      <TouchableOpacity
        ref={buttonRef}
        testID="hub-button-course-switcher"
        accessibilityLabel={t('hub.switchCourse')}
        accessibilityRole="button"
        onPress={handleOpen}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Ionicons name="swap-horizontal" size={size} color={color} />
      </TouchableOpacity>

      <Modal
        visible={dropdownVisible}
        transparent
        animationType="fade"
        onRequestClose={handleClose}
      >
        <TouchableOpacity
          testID="course-switcher-backdrop"
          style={styles.backdrop}
          activeOpacity={1}
          onPress={handleClose}
        >
          <View
            style={[
              styles.dropdown,
              {
                backgroundColor: theme.card,
                borderColor: theme.border,
                top: buttonLayout.y + buttonLayout.height + 4,
                right: 16,
                minWidth: 220,
              },
            ]}
          >
            {/* Header */}
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_XS * fontScale,
                fontWeight: '600',
                color: theme.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                paddingHorizontal: 14,
                paddingTop: 10,
                paddingBottom: 4,
                ...ffs,
              }}
            >
              {t('hub.switchCourse')}
            </Text>

            {coursePreferences.map((pref) => {
              const isActive = pref.courseId === activeCourseId;
              const src = getLanguageDisplayName(getBaseLanguage(pref.sourceLocale), uiLocale);
              const tgt = getLanguageDisplayName(getBaseLanguage(pref.targetLocale), uiLocale);

              return (
                <TouchableOpacity
                  key={pref.courseId}
                  testID={`course-switcher-option-${pref.courseId}`}
                  accessibilityLabel={`${src} → ${tgt}`}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: isActive }}
                  onPress={() => handleSelect(pref.courseId)}
                  activeOpacity={0.7}
                  style={[
                    styles.option,
                    {
                      backgroundColor: isActive ? theme.primarySoft : 'transparent',
                    },
                  ]}
                >
                  <Ionicons
                    name={isActive ? 'radio-button-on' : 'radio-button-off'}
                    size={18 * fontScale}
                    color={isActive ? theme.primary : theme.textSecondary}
                    style={{ marginRight: 10 }}
                  />
                  <Text
                    allowFontScaling={false}
                    style={{
                      fontSize: FS_SM * fontScale,
                      fontWeight: isActive ? '600' : '400',
                      color: isActive ? theme.primary : theme.text,
                      flex: 1,
                      ...ffs,
                    }}
                  >
                    {src} → {tgt}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
});

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
  },
  dropdown: {
    position: 'absolute',
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 8,
    marginHorizontal: 4,
    marginVertical: 2,
  },
});
